import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import MultiSelectDropdown from '../../views/admin/dataTables/components/MultiSelectDropdown';
import api from '../../utils/api';

const UserAssignmentModal = ({ isOpen, onClose, userData, currentUser }) => {
  const [assignmentData, setAssignmentData] = useState({
    schools: [],
    courses: [],
    classes: [],
    instructors: [],
  });

  const [filterOptions, setFilterOptions] = useState({
    schools: [],
    courses: [],
    instructors: [],
  });

  const [currentAssignments, setCurrentAssignments] = useState({
    schools: [],
    courses: [],
    classes: [],
    instructors: [],
  });

  const [loading, setLoading] = useState(false);

  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Fetch user's current assignments and filter options when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !userData?.userId) return;

      try {
        // Fetch user's current assignments
        const userResponse = await api.get(
          `/dashboard/user/profile/academic-details?userId=${userData.userId}`,
        );

        // Fetch filter options (schools, courses, instructors)
        const currentInstitution = JSON.parse(
          localStorage.getItem('institution'),
        );
        const filterResponse = await api.get(
          `/dashboard/users/getUserPageAdminFilters?institution=${currentInstitution?._id}`,
        );

        if (userResponse?.data?.success && userResponse?.data?.data) {
          const userAcademicData = userResponse.data.data;

          console.log('userAcademicData', userAcademicData);

          // Map current assignments
          const mappedAssignments = {
            schools:
              userAcademicData.schools?.map((school) => school._id) || [],
            courses:
              userAcademicData.courses?.map((course) => course._id) || [],
            classes: userAcademicData.classes?.map((cls) => cls._id) || [],
            instructors: userAcademicData.instructors || [], // instructors are already email strings
          };

          setCurrentAssignments(mappedAssignments);
          setAssignmentData(mappedAssignments); // Pre-populate modal with current assignments
        }

        if (filterResponse?.data?.success && filterResponse?.data?.data) {
          setFilterOptions({
            schools: filterResponse.data.data.schools || [],
            courses: filterResponse.data.data.courses || [],
            instructors: filterResponse.data.data.instructors || [],
          });
        }
      } catch (error) {
        console.error('Error fetching user assignments:', error);
        toast({
          title: 'Error',
          description: 'Failed to load user assignment data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    };

    fetchData();
  }, [isOpen, userData?.userId, toast]);

  // Get classes for selected courses (reuse existing logic)
  const getClassesForSelectedCourses = () => {
    const selectedCourseIds = assignmentData.courses;
    const allClasses = [];

    selectedCourseIds.forEach((courseId) => {
      const course = filterOptions.courses.find((c) => c._id === courseId);
      if (course && course.classes) {
        course.classes.forEach((cls) => {
          allClasses.push({
            value: cls._id,
            label: `${course.name} - ${cls.name}`,
            courseId: courseId,
            courseName: course.name,
            className: cls.name,
          });
        });
      }
    });

    return allClasses;
  };

  const handleAssignmentChange = (type, values) => {
    setAssignmentData((prev) => ({
      ...prev,
      [type]: values,
    }));

    // Reset classes when courses change
    if (type === 'courses') {
      // Only keep classes that belong to selected courses
      const validClasses = getClassesForSelectedCourses()
        .filter((cls) => values.includes(cls.courseId))
        .map((cls) => cls.value);

      setAssignmentData((prev) => ({
        ...prev,
        classes: prev.classes.filter((classId) =>
          validClasses.includes(classId),
        ),
      }));
    }
  };

  const hasChanges = () => {
    return (
      JSON.stringify(assignmentData.schools.sort()) !==
        JSON.stringify(currentAssignments.schools.sort()) ||
      JSON.stringify(assignmentData.courses.sort()) !==
        JSON.stringify(currentAssignments.courses.sort()) ||
      JSON.stringify(assignmentData.classes.sort()) !==
        JSON.stringify(currentAssignments.classes.sort()) ||
      JSON.stringify(assignmentData.instructors.sort()) !==
        JSON.stringify(currentAssignments.instructors.sort())
    );
  };

  const handleSubmit = async () => {
    if (!hasChanges()) {
      toast({
        title: 'No Changes',
        description: 'No changes were made to the assignments',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Prepare assignment payload
      const assignmentPayload = {
        schools: assignmentData.schools,
        courses: assignmentData.courses,
        classes: assignmentData.classes,
        instructors: assignmentData.instructors,
      };

      console.log('Assignment payload:', assignmentPayload);

      // Call update API
      await api.patch(
        `/dashboard/user/profile/academic-details?userId=${userData.userId}`,
        assignmentPayload,
      );

      toast({
        title: 'Assignments Updated',
        description: 'Academic details have been successfully updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      handleClose();
    } catch (error) {
      console.error('Error updating academic details:', error);
      toast({
        title: 'Error',
        description: 'Failed to update academic details',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset to current assignments when closing
      setAssignmentData({ ...currentAssignments });
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay bg="#53535399" />
      <ModalContent bg={bgColor} borderColor={borderColor} borderWidth="1px">
        <ModalHeader>Update Academic Details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Update {userData?.name}'s academic details to schools, courses,
            classes, and instructors
          </Text>

          <VStack spacing={4} align="stretch">
            <MultiSelectDropdown
              label="Schools"
              options={filterOptions.schools.map((school) => ({
                value: school._id,
                label: school.name,
              }))}
              selectedValues={assignmentData.schools}
              onChange={(values) => handleAssignmentChange('schools', values)}
              placeholder="Select schools"
              width="100%"
            />

            <MultiSelectDropdown
              label="Courses"
              options={filterOptions.courses.map((course) => ({
                value: course._id,
                label: course.name,
              }))}
              selectedValues={assignmentData.courses}
              onChange={(values) => handleAssignmentChange('courses', values)}
              placeholder="Select courses"
              width="100%"
            />

            {assignmentData.courses.length > 0 && (
              <MultiSelectDropdown
                label="Classes"
                options={getClassesForSelectedCourses()}
                selectedValues={assignmentData.classes}
                onChange={(values) => handleAssignmentChange('classes', values)}
                placeholder="Select classes"
                width="100%"
              />
            )}

            {/* Instructors dropdown - only show for admin and super_admin */}
            {(currentUser?.role?.toLowerCase() === 'admin' ||
              currentUser?.role?.toLowerCase() === 'super_admin') && (
              <MultiSelectDropdown
                label="Instructors"
                options={filterOptions.instructors.map((instructor) => ({
                  value: instructor.email,
                  label: instructor.fullName,
                }))}
                selectedValues={assignmentData.instructors}
                onChange={(values) =>
                  handleAssignmentChange('instructors', values)
                }
                placeholder="Select instructors"
                width="100%"
              />
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="outline"
              onClick={handleClose}
              borderRadius="8px"
              borderColor="#AAAAAA"
              height="36px"
              fontSize="14px"
              fontWeight="700"
              isDisabled={loading}
            >
              Cancel
            </Button>
            <Button
              bg="#EF6506"
              color="white"
              onClick={handleSubmit}
              borderRadius="8px"
              height="36px"
              fontSize="14px"
              fontWeight="700"
              _hover="none"
              isLoading={loading}
              isDisabled={!hasChanges()}
            >
              Update Academic Details
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UserAssignmentModal;
